import {BusinessTransactionChannel} from '@afriex/core/domain/enums';

/**
 * Declarative field registry behind the settlement account form.
 *
 * The channel picker, the subform, the validator and the list columns are all
 * driven by the entries below, so supporting a channel is one entry here (plus
 * its server validator) and no change to any of those three.
 */
export type SettlementChannelField = {
  /** Key inside the method's `info` object, e.g. `bankName`. */
  name: string;
  label: string;
  placeholder?: string;
  /** Input type; `tel`/`email` only change the mobile keyboard. */
  type?: 'text' | 'email' | 'tel';
  /** Renders a `Select` instead of an `Input` when present. */
  options?: {key: string; value: string}[];
  /** Refused when empty, rather than left off the payload. */
  required?: boolean;
  /**
   * Checked only when a value is present, and only where the rule is cheap and
   * local (an ISO 9362 pattern, an enum). The server stays the authority; this
   * exists to fail before the round trip, not to replace it.
   */
  validate?: (value: string) => string | undefined;
  /** Applied before validating and saving, e.g. uppercasing a BIC. */
  normalize?: (value: string) => string;
};

export type SettlementChannelForm = {
  channel: BusinessTransactionChannel;
  label: string;
  fields: SettlementChannelField[];
};

// ISO 9362: a SWIFT/BIC is 8 or 11 characters, letters and digits only.
const bicPattern = /^[A-Z0-9]{8}([A-Z0-9]{3})?$/;
const validateBic = (value: string): string | undefined =>
  bicPattern.test(value)
    ? undefined
    : 'A SWIFT/BIC code is 8 or 11 letters and digits, e.g. CHASUS33';
const upper = (value: string): string => value.toUpperCase();

const agentTypes = ['PAYBILL', 'TILL'];

/**
 * Bank account, and the ACH flavour of it: the server validates both with
 * `BankInfoValidator`, which asks for a bank name and an account number, so
 * that is all the operator is asked for too. SWIFT is the channel that adds the
 * routing details.
 */
const bankAccountFields: SettlementChannelField[] = [
  {
    name: 'accountName',
    label: 'Account name',
    placeholder: 'Afriex Inc.',
    required: true,
  },
  {
    name: 'accountNumber',
    label: 'Account number',
    placeholder: '8310042255',
    required: true,
  },
  {
    name: 'bankName',
    label: 'Bank',
    placeholder: 'JPMorgan Chase Bank, N.A.',
    required: true,
  },
];

const swiftFields: SettlementChannelField[] = [
  ...bankAccountFields,
  {
    name: 'swiftCode',
    // `swiftCode`, not core's `bankSwiftCode`: the server validates SWIFT
    // through `BankInfoValidator`, which reads the `AfriexBankInfo` spelling.
    label: 'SWIFT / BIC',
    placeholder: 'CHASUS33',
    required: true,
    normalize: upper,
    validate: validateBic,
  },
  {
    name: 'routingNumber',
    label: 'Routing number (optional)',
    placeholder: '021000021',
  },
];

const mobileMoneyFields: SettlementChannelField[] = [
  {
    name: 'accountName',
    label: 'Account name',
    placeholder: 'Afriex Inc.',
    required: true,
  },
  {
    name: 'accountPhone',
    label: 'Phone number',
    placeholder: '+2348012345678',
    type: 'tel',
    required: true,
  },
  {
    name: 'provider',
    label: 'Operator',
    placeholder: 'MTN',
    required: true,
  },
];

const interacFields: SettlementChannelField[] = [
  {
    name: 'accountName',
    label: 'Account name',
    placeholder: 'Afriex Inc.',
    required: true,
  },
  {
    name: 'email',
    label: 'Email',
    placeholder: 'payments@afriex.com',
    type: 'email',
    required: true,
  },
  {
    name: 'question',
    label: 'Security question',
    placeholder: 'What is the invoice reference?',
    required: true,
  },
  {
    name: 'answer',
    label: 'Security answer',
    required: true,
  },
];

const upiFields: SettlementChannelField[] = [
  {
    name: 'accountName',
    label: 'Account name',
    placeholder: 'Afriex Inc.',
    required: true,
  },
  {
    name: 'accountPhone',
    label: 'Phone number',
    placeholder: '+919876543210',
    type: 'tel',
    required: true,
  },
  {
    name: 'upiId',
    label: 'UPI ID',
    placeholder: 'afriex@okhdfcbank',
    required: true,
  },
  {
    name: 'email',
    label: 'Email (optional)',
    placeholder: 'payments@afriex.com',
    type: 'email',
  },
];

const paybillTillFields: SettlementChannelField[] = [
  {
    name: 'accountName',
    label: 'Account name',
    placeholder: 'Afriex Inc.',
    required: true,
  },
  {
    name: 'agentId',
    label: 'Paybill / Till number',
    placeholder: '247247',
    required: true,
  },
  {
    name: 'agentType',
    label: 'Agent type',
    required: true,
    options: agentTypes.map((type) => ({key: type, value: type})),
    normalize: upper,
    validate: (value) =>
      agentTypes.includes(value) ? undefined : 'Agent type is PAYBILL or TILL',
  },
  {
    name: 'provider',
    label: 'Operator',
    placeholder: 'Safaricom',
    required: true,
  },
  {
    name: 'accountPhone',
    label: 'Phone number (optional)',
    placeholder: '+254712345678',
    type: 'tel',
  },
];

const weChatFields: SettlementChannelField[] = [
  {
    name: 'accountName',
    label: 'Account name',
    placeholder: 'Afriex Inc.',
    required: true,
  },
  {
    name: 'accountPhone',
    label: 'Phone number',
    placeholder: '+8613800138000',
    type: 'tel',
    required: true,
  },
  {
    name: 'provider',
    label: 'Wallet',
    placeholder: 'WeChat',
    required: true,
  },
  {
    name: 'wechatId',
    label: 'WeChat ID (optional)',
    placeholder: 'conrad2011',
  },
];

/**
 * The channels an invoice can be paid into. Card channels are deliberately
 * absent: a card is a funding instrument a payer uses, never a destination
 * Afriex publishes on an invoice, and `CARD`'s `info` (last4, fingerprint) is
 * not something an operator could type in anyway. `ALIPAY` and the pool/virtual
 * channels are absent for the same reason plus a server gap: only `WE_CHAT`
 * reaches `WeChatInfoValidator`, so anything else sharing that info shape would
 * be validated as a bank account.
 *
 * Order is the picker order; the first entry is the create-form default.
 */
export const settlementChannelForms: SettlementChannelForm[] = [
  {
    channel: BusinessTransactionChannel.BANK_ACCOUNT,
    label: 'Bank account',
    fields: bankAccountFields,
  },
  {
    channel: BusinessTransactionChannel.ACH_BANK_ACCOUNT,
    label: 'ACH bank account',
    fields: bankAccountFields,
  },
  {
    channel: BusinessTransactionChannel.SWIFT,
    label: 'SWIFT',
    fields: swiftFields,
  },
  {
    channel: BusinessTransactionChannel.MOBILE_MONEY,
    label: 'Mobile money',
    fields: mobileMoneyFields,
  },
  {
    channel: BusinessTransactionChannel.INTERAC,
    label: 'Interac e-Transfer',
    fields: interacFields,
  },
  {
    channel: BusinessTransactionChannel.UPI,
    label: 'UPI',
    fields: upiFields,
  },
  {
    channel: BusinessTransactionChannel.PAYBILL_TILL,
    label: 'Paybill / Till',
    fields: paybillTillFields,
  },
  {
    channel: BusinessTransactionChannel.WE_CHAT,
    label: 'WeChat Pay',
    fields: weChatFields,
  },
];

/**
 * Fields every channel carries. The narration an invoice reproduces lives in
 * `info` rather than `meta` because the admin whitelist exposes `info` whole
 * but only `meta.flags`, so a value in `meta` would never come back.
 */
export const settlementSharedFields: SettlementChannelField[] = [
  {
    name: 'instructions',
    label: 'Payment instructions',
    placeholder: 'Quote the invoice reference as the payment narration.',
  },
];

export const defaultSettlementChannel = BusinessTransactionChannel.BANK_ACCOUNT;

export const getSettlementChannelForm = (
  channel: BusinessTransactionChannel,
): SettlementChannelForm | undefined =>
  settlementChannelForms.find((form) => form.channel === channel);

/** The fields the channel itself asks for, without the shared ones. */
export const settlementChannelFormFields = (
  channel: BusinessTransactionChannel,
): SettlementChannelField[] => getSettlementChannelForm(channel)?.fields ?? [];

/** A channel's own fields followed by the shared ones, in render order. */
export const settlementChannelFields = (
  channel: BusinessTransactionChannel,
): SettlementChannelField[] => [
  ...settlementChannelFormFields(channel),
  ...settlementSharedFields,
];

export const settlementChannelLabel = (
  channel: BusinessTransactionChannel,
): string => getSettlementChannelForm(channel)?.label ?? channel;
